import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Plus, Edit2, Trash2, Search, Activity, Eye, Filter, Calendar, X } from 'lucide-react';
import AddDelivery from './AddDelivery';
import TakeDeliveryActionModal from './TakeDeliveryActionModal';
import ViewDeliveryModal from './ViewDeliveryModal';
import Dropdown from '../../components/Dropdown';
import DateFilter from '../../components/DateFilter';
import toast from 'react-hot-toast';
import { formatAppDate, isDateInRange } from '../../utils/dateUtils';
import { getStatusColors } from '../../utils/statusUtils';
import { useAuth } from '../../context/AuthContext';

const DeliveryList = () => {
  const { user, hasPermission } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionDelivery, setActionDelivery] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingDelivery, setViewingDelivery] = useState(null);

  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [statusOptions, setStatusOptions] = useState([
    { value: 'All', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Cancelled', label: 'Cancelled' }
  ]);

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const res = await api.get('/deliveries?limit=1000');
      if (res.data && res.data.data) {
        setDeliveries(res.data.data);
      } else if (Array.isArray(res.data)) {
        setDeliveries(res.data);
      } else {
        setDeliveries([]);
      }
    } catch (error) {
      console.error("Error fetching deliveries:", error);
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveries();
    const fetchStatuses = async () => {
      try {
        const res = await api.get('/masters/delivery-statuses');
        const rawStatuses = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const activeStatuses = rawStatuses
          .filter(s => s.status === 'Active')
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        if (activeStatuses.length > 0) {
          setStatusOptions([
            { value: 'All', label: 'All Statuses' },
            ...activeStatuses.map(s => ({ value: s.name, label: s.name }))
          ]);
        }
      } catch (err) {
        console.error("Failed to fetch delivery status options", err);
      }
    };
    fetchStatuses();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this delivery?")) {
      try {
        await api.delete(`/deliveries/${id}`);
        toast.success("Delivery deleted");
        fetchDeliveries();
      } catch (e) {
        toast.error("Failed to delete delivery");
      }
    }
  };

  const getStatusBadge = (status) => {
    const colors = getStatusColors(status);
    return (
      <span style={{
        padding: '0.25rem 0.6rem', 
        borderRadius: '9999px', 
        backgroundColor: colors.bg,
        color: colors.text,
        fontSize: '0.75rem',
        fontWeight: '600',
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap'
      }}>
        {status}
      </span>
    );
  };

  const canCreate = user?.role === 'Admin' || (hasPermission('deliveries:write') && user?.role !== 'Delivery Boy');
  const canEdit = user?.role === 'Admin' || (hasPermission('deliveries:write') && user?.role !== 'Delivery Boy');
  const canDelete = user?.role === 'Admin' || (hasPermission('deliveries:delete') && user?.role !== 'Delivery Boy');

  // Filter Deliveries based on Search, Status, and Scheduled Date
  const filteredDeliveries = deliveries.filter(d => {
    // 1. Search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      const idMatch = `#${d.id}`.toLowerCase().includes(term) || d.id.toString() === term;
      const titleMatch = (d.title || '').toLowerCase().includes(term);
      const companyMatch = (d.lead?.companyName || '').toLowerCase().includes(term);
      const contactMatch = (d.lead?.contactPerson || '').toLowerCase().includes(term);
      const phoneMatch = (d.lead?.phone || '').toLowerCase().includes(term);
      const cityMatch = (d.lead?.city || '').toLowerCase().includes(term);
      const boyMatch = (d.deliveryBoy?.name || '').toLowerCase().includes(term);
      const detailsMatch = (d.details || '').toLowerCase().includes(term);
      const orderNoMatch = (d.orderNo || '').toLowerCase().includes(term);
      const partyNameMatch = (d.partyName || '').toLowerCase().includes(term);
      const partyPhoneMatch = (d.partyPhone || '').toLowerCase().includes(term);
      const partyAddressMatch = (d.partyAddress || '').toLowerCase().includes(term);
      const partyCityMatch = (d.partyCity || '').toLowerCase().includes(term);

      if (!idMatch && !titleMatch && !companyMatch && !contactMatch && !phoneMatch && !cityMatch && !boyMatch && !detailsMatch && !orderNoMatch && !partyNameMatch && !partyPhoneMatch && !partyAddressMatch && !partyCityMatch) {
        return false;
      }
    }

    // 2. Status filter
    if (statusFilter && statusFilter !== 'All') {
      if ((d.status || '').toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }
    }

    // 3. Scheduled Date Range filter
    if (dateRangeFilter && dateRangeFilter !== 'all') {
      if (!isDateInRange(d.scheduledDate, dateRangeFilter)) {
        return false;
      }
    }

    return true;
  });

  const hasActiveFilters = searchTerm.trim() || (statusFilter && statusFilter !== 'All') || (dateRangeFilter && dateRangeFilter !== 'all');

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Delivery Management</h1>
          <p style={{ color: '#6b7280' }}>Manage deliveries and assign delivery boys</p>
        </div>
        {canCreate && (
          <button className="btn btn-primary" onClick={() => { setEditingDelivery(null); setShowAddModal(true); }}>
            <Plus size={18} style={{ marginRight: '0.5rem' }} /> Add Delivery
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
          
          {/* Search Input */}
          <div style={{ position: 'relative', flex: '1 1 250px', minWidth: '220px' }}>
            <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search by Delivery #, Order #, title, party name, phone, city, delivery boy..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
              style={{ width: '100%', paddingLeft: '2.5rem', paddingRight: '1rem', paddingTop: '0.6rem', paddingBottom: '0.6rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}
            />
          </div>

          {/* Status & Date Filters */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
            
            {/* Status Dropdown */}
            <div style={{ width: '160px' }}>
              <Dropdown
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={statusOptions}
                style={{ border: '1px solid var(--color-border)', borderRadius: '8px' }}
              />
            </div>

            {/* Date Range Dropdown Filter */}
            <DateFilter 
              value={dateRangeFilter} 
              onChange={(val) => setDateRangeFilter(val)} 
            />

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={() => { setSearchTerm(''); setStatusFilter('All'); setDateRangeFilter('all'); }}
                className="btn"
                style={{ padding: '0.55rem 1rem', border: '1px solid #cbd5e1', borderRadius: '8px', backgroundColor: '#f8fafc', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                <X size={14} /> Clear
              </button>
            )}
          </div>

        </div>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>Loading deliveries...</div>
        ) : (
          <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '1rem' }}>Delivery No.</th>
                  <th style={{ padding: '1rem' }}>Order Info</th>
                  <th style={{ padding: '1rem' }}>Party / Customer Details</th>
                  <th style={{ padding: '1rem' }}>Assigned To</th>
                  <th style={{ padding: '1rem' }}>Status</th>
                  <th style={{ padding: '1rem' }}>Scheduled Date</th>
                  <th style={{ padding: '1rem' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeliveries.length > 0 ? filteredDeliveries.map(d => {
                  const isCompleted = ['delivered', 'cancelled'].includes((d.status || '').trim().toLowerCase());
                  const partyName = d.partyName || d.lead?.companyName || d.lead?.contactPerson || '';
                  const displayPhone = d.partyPhone || d.lead?.phone;
                  const displayEmail = d.partyEmail || d.lead?.email;
                  const rawAddress = d.partyAddress || d.lead?.address || '';
                  const cleanAddress = rawAddress ? rawAddress.replace(/\s*\[GPS:\s*Lat:\s*[\d.-]+,\s*Lon:\s*[\d.-]+\]/gi, '').trim() : '';
                  const addressParts = [
                    cleanAddress,
                    d.partyCity || d.lead?.city,
                    d.partyState || d.lead?.state,
                    d.partyPincode || d.lead?.pincode
                  ].filter(Boolean);
                  const fullAddress = addressParts.join(', ');

                  return (
                    <tr key={d.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                        <div
                          onClick={() => { setViewingDelivery(d); setShowViewModal(true); }}
                          style={{ cursor: 'pointer', display: 'inline-block' }}
                          title="Click to view delivery details"
                        >
                          <div style={{ fontWeight: '700', color: '#0284c7', fontSize: '0.95rem' }}>#{d.id}</div>
                          {d.title && (
                            <div style={{ fontSize: '0.85rem', color: '#334155', marginTop: '0.2rem', fontWeight: '500' }}>
                              {d.title}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                        {d.orderNo ? (
                          <div style={{ fontWeight: '600', color: '#0369a1' }}>{d.orderNo}</div>
                        ) : (
                          <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem' }}>No Order #</div>
                        )}
                        {d.orderDate && (
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.15rem' }}>
                            📅 {formatAppDate(d.orderDate).split(' ')[0]}
                          </div>
                        )}
                        {d.amount !== undefined && d.amount !== null && d.amount !== '' && (
                          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#059669', marginTop: '0.2rem' }}>
                            ₹{Number(d.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                        <div style={{ fontWeight: '600', color: 'var(--color-text)' }}>
                          {partyName || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No Party Name</span>}
                        </div>
                        {d.lead && partyName && partyName.toLowerCase() !== (d.lead.companyName || '').toLowerCase() && (
                          <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>
                            Lead: {d.lead.companyName || d.lead.contactPerson}
                          </div>
                        )}
                        <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.25rem' }}>
                          {displayPhone && <span>📞 {displayPhone}</span>}
                          {displayEmail && <span>✉️ {displayEmail}</span>}
                          {fullAddress && <span style={{ color: '#4b5563' }}>📍 {fullAddress}</span>}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                        {d.deliveryBoy ? (
                          <div>
                            <div style={{ fontWeight: '500', color: 'var(--color-text)' }}>{d.deliveryBoy.name}</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{d.deliveryBoy.phone}</div>
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Unassigned</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>{getStatusBadge(d.status)}</td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>{d.scheduledDate ? formatAppDate(d.scheduledDate) : '-'}</td>
                      <td style={{ padding: '1rem', verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button style={{ color: '#0284c7', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} onClick={() => { setViewingDelivery(d); setShowViewModal(true); }} title="View Details & Timeline">
                            <Eye size={16} />
                          </button>
                          {!isCompleted && (
                            <button style={{ color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} onClick={() => { setActionDelivery(d); setShowActionModal(true); }} title="Take Action">
                              <Activity size={16} />
                            </button>
                          )}
                          {canEdit && !isCompleted && (
                            <button style={{ color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} onClick={() => { setEditingDelivery(d); setShowAddModal(true); }} title="Edit">
                              <Edit2 size={16} />
                            </button>
                          )}
                          {canDelete && !isCompleted && (
                            <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }} onClick={() => handleDelete(d.id)} title="Delete">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>No deliveries found matching criteria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddDelivery 
          onClose={() => setShowAddModal(false)} 
          onSuccess={() => { setShowAddModal(false); fetchDeliveries(); }} 
          delivery={editingDelivery}
        />
      )}

      {showActionModal && actionDelivery && (
        <TakeDeliveryActionModal 
          delivery={actionDelivery}
          onClose={() => setShowActionModal(false)}
          onSuccess={() => { setShowActionModal(false); fetchDeliveries(); }}
        />
      )}

      {showViewModal && viewingDelivery && (
        <ViewDeliveryModal 
          delivery={viewingDelivery}
          onClose={() => setShowViewModal(false)}
          onTakeAction={(d) => { setActionDelivery(d); setShowActionModal(true); }}
          canTakeAction={true}
        />
      )}
    </div>
  );
};

export default DeliveryList;
